import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
    MAT_MOMENT_DATE_ADAPTER_OPTIONS,
    MAT_MOMENT_DATE_FORMATS,
    MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AumicoComponentsModule } from '@modeso/aumico-components';
import { AumicoCoreModule } from '@modeso/aumico-lib-core-fe';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { I18NextModule, I18NextService } from 'angular-i18next';
import { AnnotationsComponent } from './components/annotations/annotations.component';
import { ClosingWorkflowsTableComponent } from './components/closing-workflows-table/closing-workflows-table.component';
import { CompanyFilterDropDown } from './components/company-filter-drop-down/company-filter-drop-down.component';
import { DocumentsComponent } from './components/documents/documents.component';
import { DueDateFilterDropDown } from './components/due-date-filter-drop-down/due-date-filter-drop-down.component';
import { ExportDocumentsZipFilterDropDown } from './components/export-documents-zip-filter-dropdown/export-documents-zip-filter-dropdown.component';
import { ExportTasksFilterDropDown } from './components/export-tasks-filter-dropdown/export-tasks-filter-dropdown.component';
import { PmTasksOverviewTableComponent } from './components/pm-tasks-overview-table/pm-tasks-overview-table.component';
import { PmTasksTableComponent } from './components/pm-tasks-table/pm-tasks-table.component';
import { NewProjectWizardComponent } from './components/project-wizard/new-project-wizard.component';
import { NewPersonProjectWizardComponent } from './components/project-wizard/new-person-project-wizard.component';
import { StatusFilterDropDown } from './components/status-filter-drop-down/status-filter-drop-down.component';
import { SubjectTypeFilterDropDown } from './components/subject-type-filter-drop-down/subject-type-filter-drop-down.component';
import { SyncBannerComponent } from './components/sync-banner/app-sync-banner.component';
import { WorkflowConfiguratorComponent } from './components/workflow-configurator/workflow-configurator.component';
import { WorkflowFormComponent } from './components/workflow-form/workflow-form.component';
import { ANNOTATIONS_FEATURE_KEY, CPMM_UI_FEATURE, TASKS_FEATURE_KEY } from './constants/constants';
import { CpmmConfig, CpmmConfigService } from './cpmm.config';
import { LineCheckDirective } from './directives/line-check.directive';
import { AnnotationsEffects } from './effects/annotations.effects';
import { SyncEffects } from './effects/sync.effects';
import { TasksEffects } from './effects/tasks.effects';
import { WorkflowSectionsEffects } from './effects/workflow-sections.effects';
import { WorkflowsEffects } from './effects/workflows.effects';
import { AnnotationsProvider } from './providers/annotations.provider';
import * as fromAnnotationsReducer from './reducers/annotations.reducer';
import { annotationsFeature } from './reducers/annotations.reducer';
import * as fromTasksReducer from './reducers/tasks.reducer';
import { tasksFeature } from './reducers/tasks.reducer';
import { uiReducer } from './reducers/ui.reducer';
import { workflowSectionsFeature } from './reducers/workflow-sections.reducer';
import { workflowsFeature } from './reducers/workflows.reducer';
import { AnnotationsService } from './services/annotations.service';
import { SyncService } from './services/sync.service';
import { TasksService } from './services/tasks.service';
import { WorkflowSectionsService } from './services/workflow-sections.service';
import { WorkflowsService } from './services/workflows.service';
import { ReportTypeFilterDropDown } from './components/report-type-filter-drop-down/report-type-filter-drop-down.component';

export function matDateLocaleFactory(i18nextService: I18NextService): string {
    return i18nextService.language;
}

const EXPORTED_DECLARATIONS = [
    PmTasksOverviewTableComponent,
    CompanyFilterDropDown,
    DueDateFilterDropDown,
    StatusFilterDropDown,
    SubjectTypeFilterDropDown,
    ReportTypeFilterDropDown,
    NewProjectWizardComponent,
    NewPersonProjectWizardComponent,
    WorkflowConfiguratorComponent,
    ClosingWorkflowsTableComponent,
    PmTasksTableComponent,
    WorkflowFormComponent,
    AnnotationsComponent,
    LineCheckDirective,
    DocumentsComponent,
    ExportTasksFilterDropDown,
    ExportDocumentsZipFilterDropDown,
    SyncBannerComponent,
];

const ANGULAR_MATERIAL = [MatDatepickerModule, MatFormFieldModule, MatProgressSpinnerModule];

@NgModule({
    declarations: EXPORTED_DECLARATIONS,
    imports: [
        CommonModule,
        I18NextModule,
        AumicoComponentsModule,
        FormsModule,
        ReactiveFormsModule,
        ANGULAR_MATERIAL,
        StoreModule.forFeature(annotationsFeature),
        StoreModule.forFeature(ANNOTATIONS_FEATURE_KEY, fromAnnotationsReducer.reducer),
        EffectsModule.forFeature([
            AnnotationsEffects,
            TasksEffects,
            WorkflowSectionsEffects,
            WorkflowsEffects,
            SyncEffects,
        ]),
        StoreModule.forFeature(tasksFeature),
        StoreModule.forFeature(TASKS_FEATURE_KEY, fromTasksReducer.reducer),
        StoreModule.forFeature(workflowsFeature),
        StoreModule.forFeature(workflowSectionsFeature),
        StoreModule.forFeature(CPMM_UI_FEATURE, uiReducer),
        AumicoComponentsModule,
        AumicoCoreModule,
        FormsModule,
        ReactiveFormsModule,
        DragDropModule,
        ANGULAR_MATERIAL,
    ],
    exports: EXPORTED_DECLARATIONS,
    providers: [
        AnnotationsProvider,
        TasksService,
        WorkflowsService,
        WorkflowSectionsService,
        SyncService,
        { provide: MAT_DATE_LOCALE, useFactory: matDateLocaleFactory, deps: [I18NextService] },
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
        },
        { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
    ],
})
export class AumicoCpmmModule {
    static forRoot(config: CpmmConfig): ModuleWithProviders<AumicoCpmmModule> {
        return {
            ngModule: AumicoCpmmModule,
            providers: [
                AnnotationsService,
                TasksService,
                WorkflowsService,
                WorkflowSectionsService,
                SyncService,
                {
                    provide: CpmmConfigService,
                    useValue: config,
                },
            ],
        };
    }
}
